<template>
    <div class="pv pvn">

        <!-- ════════════════════ CABECERA ════════════════════ -->
        <div class="pv-dash-header">
            <div class="pv-dash-header-top">
                <div>
                    <div class="pv-eyebrow">TALLER · VERO {{ esB2B ? 'B2B' : 'PRODUCTION' }}</div>
                    <h1 class="pv-title">Nuevo pedido</h1>
                </div>
                <div class="pv-header-actions">
                    <div class="pv-stat-pill">
                        <i class="fa-solid fa-layer-group"></i>
                        <strong>{{ pedidosSoldOut.length }}</strong>
                        <span>SKUs</span>
                    </div>
                    <div class="pv-stat-pill">
                        <i class="fa-solid fa-boxes-stacked"></i>
                        <strong>{{ totalUnidades }}</strong>
                        <span>uds</span>
                    </div>
                    <button class="pv-btn pv-btn--ghost" @click="$router.push(rutaListado)">
                        <i class="fa-solid fa-arrow-left"></i>
                        <span>Volver</span>
                    </button>
                    <button v-if="!esB2B" class="pv-btn pv-btn--ghost" :disabled="!articulosCargados" @click="procesarClipBoard()">
                        <i class="fa-solid fa-clipboard"></i>
                        <span>Procesar Sold Out</span>
                    </button>
                    <button class="pv-btn pv-btn--primary" :disabled="totalUnidades === 0 || creando" @click="crearPedido()">
                        <b-spinner v-if="creando" small></b-spinner>
                        <i v-else class="fa-solid fa-check"></i>
                        <span>Crear pedido</span>
                    </button>
                </div>
            </div>

            <!-- Datos del pedido -->
            <div class="pvn-form">
                <div v-if="esB2B" class="pvn-field pvn-field--wide">
                    <label>Pedido del escáner</label>
                    <v-select
                        v-model="pedidoEscanerSel"
                        :options="pedidosEscaner"
                        placeholder="Seleccionar pedido..."
                        class="nv-select"
                        @input="aplicarPedidoEscaner"
                    />
                </div>

                <div class="pvn-field">
                    <label>Company / Cliente</label>
                    <div class="pv-search pvn-input">
                        <i class="fa-solid fa-building pv-search-icon"></i>
                        <input v-model="company" type="text" placeholder="Nombre del cliente" />
                    </div>
                </div>

                <div class="pvn-field">
                    <label>Agregar SKU</label>
                    <div class="pvn-inline">
                        <div class="pv-search pvn-input">
                            <i class="fa-solid fa-barcode pv-search-icon"></i>
                            <input
                                v-model="skuManual"
                                type="text"
                                placeholder="Ej: DOPP-KIT o DOPP-KIT-TAN"
                                :disabled="!articulosCargados"
                                @keyup.enter="agregarSkuManual()"
                            />
                        </div>
                        <button class="pv-btn pv-btn--ghost" :disabled="!articulosCargados || !skuManual.trim()" @click="agregarSkuManual()">
                            <i class="fa-solid fa-plus"></i>
                            <span>Agregar</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- ════════════════════ LÍNEAS ════════════════════ -->
        <section class="pv-card">
            <header class="pv-card-head">
                <div class="pv-card-title">
                    <span class="pv-chevron">›</span>
                    <span>Líneas del pedido</span>
                </div>
                <div class="pvn-legend">
                    <span class="pvn-tag pvn-tag--stock">Stock</span>
                    <span class="pvn-tag pvn-tag--prev">Previsión</span>
                    <span class="pvn-tag pvn-tag--fabri">Fabricando</span>
                    <span class="pvn-tag pvn-tag--orders">Orders</span>
                </div>
            </header>

            <b-table
                :items="pedidosSoldOut"
                :fields="columnasVisibles"
                class="pvn-table"
                responsive
                sticky-header="calc(100vh - 330px)"
                no-border-collapse
                show-empty
                :busy="!articulosCargados"
            >
                <template #table-busy>
                    <div class="pvn-empty">
                        <b-spinner small class="mr-1"></b-spinner>
                        <span>Cargando artículos y previsiones...</span>
                    </div>
                </template>

                <template #empty>
                    <div class="pvn-empty">
                        <i class="fa-solid fa-box-open"></i>
                        <span v-if="esB2B">Elige un pedido del escáner o agrega un SKU</span>
                        <span v-else>Agrega un SKU o pulsa <b>Procesar Sold Out</b></span>
                    </div>
                </template>

                <template #cell(sku)="data">
                    <div class="pvn-sku">
                        <span class="pvn-sku-code">{{ data.item.sku }}</span>
                        <span v-if="unidadesFila(data.item)" class="pvn-sku-uds">{{ unidadesFila(data.item) }} uds</span>
                    </div>
                </template>

                <template v-for="columna in columnasColores" #[`cell(${columna.key})`]="data">
                    <div
                        v-if="data.item[columna.key] !== undefined"
                        :key="columna.key"
                        class="pvn-cell"
                        :class="{
                            'pvn-cell--orders': data.item[columna.key.replace('pedidos_', 'esperando_')] > 0,
                            'pvn-cell--activa': data.item[columna.key] > 0
                        }"
                    >
                        <span
                            v-if="data.item[columna.key.replace('pedidos_', 'stock_')] !== undefined"
                            class="pvn-stock"
                            :class="parseInt(data.item[columna.key.replace('pedidos_', 'stock_')]) < 1 ? 'is-out' : 'is-ok'"
                            title="Stock"
                        ><em>Stock</em>{{ data.item[columna.key.replace('pedidos_', 'stock_')] }}</span>

                        <input
                            v-model.number="data.item[columna.key]"
                            type="number"
                            min="0"
                            class="pvn-qty"
                            @wheel.prevent
                        />

                        <div class="pvn-meta">
                            <span
                                v-if="data.item[columna.key.replace('pedidos_', 'prevision_')] !== undefined"
                                class="pvn-prev"
                                title="Previsión"
                            ><em>Prev</em>{{ data.item[columna.key.replace('pedidos_', 'prevision_')] || 0 }}</span>
                            <span
                                v-if="data.item[columna.key.replace('pedidos_', 'fabricando_')] > 0"
                                class="pvn-fabri"
                                title="Fabricando"
                            ><em>Fab</em>{{ data.item[columna.key.replace('pedidos_', 'fabricando_')] }}</span>
                            <span
                                v-if="data.item[columna.key.replace('pedidos_', 'esperando_')] > 0"
                                class="pvn-orders"
                                title="Orders esperando"
                            ><em>Ord</em>{{ data.item[columna.key.replace('pedidos_', 'esperando_')] }}</span>
                        </div>
                    </div>
                    <span v-else :key="columna.key" class="pvn-na">—</span>
                </template>

                <template #cell(acciones)="data">
                    <span class="action-btn pvn-remove" title="Quitar SKU" @click="quitarFila(data.item)">
                        <i class="fa-solid fa-xmark"></i>
                    </span>
                </template>
            </b-table>
        </section>
    </div>
</template>

<script>
import axios from "@axios";
/*import VueApexCharts from 'vue-apexcharts'*/
import {
    BTable,
    BFormCheckbox,
    BFormInput,
    BFormGroup,
    BModal,
    BCardText,
    BCardBody,
    BCard,
    BRow,
    BCol,
    BSpinner,
} from "bootstrap-vue";
import BCardCode from "@core/components/b-card-code";
import { $themeColors } from "@themeConfig";
import { useTimeout } from "@vueuse/shared";
import vSelect from "vue-select";
import { forEach } from "postcss-rtl/lib/affected-props";
import flatPickr from "vue-flatpickr-component";

export default {
    components: {
        BCardCode,
        BFormCheckbox,
        BTable,
        BFormInput,
        BFormGroup,
        BCardText,
        BCardBody,
        BCard,
        BRow,
        BCol,
        vSelect,
        flatPickr,
        BSpinner,
    },
    data() {
        return {
            columnasTable: [
                {
                    key: "sku",
                    label: "Sku",
                    sortable: true,
                },
                { key: "pedidos_tan", label: "TAN" },
                { key: "pedidos_blk", label: "BLK" },
                { key: "pedidos_db", label: "DB" },
                { key: "pedidos_navy", label: "Navy" },
                { key: "pedidos_grey", label: "Grey" },
                { key: "pedidos_mocha", label: "Mocha" },
                { key: "pedidos_olive", label: "Olive" },
                { key: "pedidos_camel", label: "Camel" },
                { key: "pedidos_caoba", label: "Caoba" },
                { key: "pedidos_brown", label: "Brown" },
                { key: "pedidos_army", label: "Army" },
                { key: "pedidos_amber", label: "Amber" },
                { key: "pedidos_stone", label: "Stone" },
                { key: "pedidos_hab", label: "Hab" },
                { key: "pedidos_khk", label: "Khk" }
            ],
            botonProcesarActivo: true,
            creando: false,
            articulosCargados: false,
            pedidosSoldOut: [],
            buscar: "",
            skuManual: "",
            company: "",
            articulos: [],
            anyos: [
                { nombre: "2026", value: 2026, active: false },
                { nombre: "2025", value: 2025, active: true },
                { nombre: "2024", value: 2024, active: true },
                { nombre: "2023", value: 2023, active: true },
                { nombre: "2022", value: 2022, active: true },
                { nombre: "2021", value: 2021, active: true },
                { nombre: "2020", value: 2020, active: true },
                { nombre: "2019", value: 2019, active: true }
            ],
            meses: [
                { nombre: "Enero", value: 1, active: true },
                { nombre: "Febrero", value: 2, active: true },
                { nombre: "Marzo", value: 3, active: true },
                { nombre: "Abril", value: 4, active: true },
                { nombre: "Mayo", value: 5, active: true },
                { nombre: "Junio", value: 6, active: true },
                { nombre: "Julio", value: 7, active: true },
                { nombre: "Agosto", value: 8, active: true },
                { nombre: "Septiembre", value: 9, active: true },
                { nombre: "Octubre", value: 10, active: true },
                { nombre: "Noviembre", value: 11, active: true },
                { nombre: "Diciembre", value: 12, active: true }
            ],
            articulosFabricando: {},
            // Pedido del escáner del que parte este pedido B2B
            pedidosEscaner: [],
            pedidoEscanerSel: null,
            escanerPedidoId: null,
        };
    },
    computed: {
        esB2B() {
            return ((this.$route.meta && this.$route.meta.tipo) || 'B2C') === 'B2B';
        },
        rutaListado() {
            return this.esB2B ? '/pedido-taller-vero-b2b' : '/pedido-taller-vero';
        },
        // Solo los colores que tiene (o se piden en) alguna de las filas
        columnasColores() {
            return this.columnasTable
                .filter(col => col.key.startsWith('pedidos_'))
                .filter(col => this.pedidosSoldOut.some(fila => fila[col.key] !== undefined))
                .map(col => ({ ...col, thClass: 'pvn-th-color', tdClass: 'pvn-td-color' }));
        },
        columnasVisibles() {
            return [
                { key: 'sku', label: 'SKU', sortable: true, thClass: 'pvn-th-sku', tdClass: 'pvn-td-sku' },
                ...this.columnasColores,
                { key: 'acciones', label: '', thClass: 'pvn-th-acc', tdClass: 'pvn-td-acc' },
            ];
        },
        totalUnidades() {
            return this.pedidosSoldOut.reduce((total, fila) => total + this.unidadesFila(fila), 0);
        },
    },
    methods: {
        cargarPedidosEscaner() {
            axios
                .get("/escaner/pedidos")
                .then(response => response.data)
                .then(pedidos => {
                    this.pedidosEscaner = (pedidos || []).map(p => {
                        const c = p.cliente || {};
                        const nombre = c.empresa || [c.nombre, c.apellidos].filter(Boolean).join(' ') || '—';
                        const fecha = p.createdAt ? new Date(p.createdAt).toLocaleDateString('es-ES') : '';
                        return { ...p, label: `${fecha} — ${nombre} — ${p.simbolo || ''}${p.total}` };
                    });
                })
                .catch(() => {});
        },
        aplicarPedidoEscaner(p) {
            if (!p) {
                this.escanerPedidoId = null;
                return;
            }

            this.escanerPedidoId = p.id;

            const c = p.cliente || {};
            this.company = c.empresa || [c.nombre, c.apellidos].filter(Boolean).join(' ');

            // Agrupar los items del pedido del escáner por raíz de SKU y color
            const porRaiz = {};
            (p.items || []).forEach(it => {
                const sku = (it.sku || '').toUpperCase().trim();
                const lastDash = sku.lastIndexOf('-');
                if (lastDash < 0) return;
                const raiz = sku.substring(0, lastDash);
                const color = sku.substring(lastDash + 1).toLowerCase();
                if (!porRaiz[raiz]) porRaiz[raiz] = {};
                porRaiz[raiz][color] = (porRaiz[raiz][color] || 0) + (parseInt(it.qty, 10) || 0);
            });

            this.pedidosSoldOut = Object.keys(porRaiz).sort().map(raiz => {
                const fila = {
                    sku: raiz,
                    totalPedidos: '0',
                    lineaPedido: true
                };

                // Variantes del maestro para stock/previsión/fabricando (igual que agregarSkuManual)
                const variantes = this.articulos.filter(art => {
                    const artSku = art.sku.toUpperCase();
                    return artSku.substring(0, artSku.lastIndexOf('-')) === raiz;
                });

                variantes.forEach(variante => {
                    const varLastDash = variante.sku.lastIndexOf('-');
                    const color = variante.sku.substring(varLastDash + 1).toLowerCase();

                    this.agregarColumnaColor(color);

                    fila[`esperando_${color}`] = 0;
                    fila[`stock_${color}`] = variante.stock || 0;
                    fila[`prevision_${color}`] = variante.prevision || 0;
                    fila[`fabricando_${color}`] = this.articulosFabricando[variante.sku] || 0;
                    fila[`pedidos_${color}`] = '';
                });

                // Cantidades pedidas en el escáner
                Object.keys(porRaiz[raiz]).forEach(color => {
                    this.agregarColumnaColor(color);
                    fila[`pedidos_${color}`] = porRaiz[raiz][color];
                });

                return fila;
            });

            this.botonProcesarActivo = false;
        },
        loadItems() {

            // axios.post("/uso", {seccion:"pedido-taller-vero"});
            axios.post("/uso", { seccion: "pedidosVero-nuevo" });

            const fetchArticulosShopify = axios.get("/articulos/shopify").then(response => response.data);
            const fetchVentasMensual = axios.get("/ventas/mensual").then(response => response.data);
            const fetchFinanzasArticulos = axios.get("/finanzas/articulos").then(response => response.data);
            const fetchArticulosPedido = axios.get("/articulos/sku/pedido").then(response => response.data);

            const fetchFabricando = axios.get("/taller/pedido/vero/fabricando").then(response => response.data);

            Promise.all([fetchArticulosShopify, fetchVentasMensual, fetchFinanzasArticulos, fetchArticulosPedido, fetchFabricando])
                .then(([articulosShopify, ventasMensual, finanzasArticulos, articulosPedido, fabricando]) => {

                    const fabriMap = {};
                    fabricando.forEach(f => {
                        fabriMap[f.sku] = f.totalFabricando;
                    });
                    this.articulosFabricando = fabriMap;

                    // Procesar datos de articulosShopify
                    const variantsArray = articulosShopify.flatMap(item =>
                        item.variants.map(variant => ({
                            sku: variant.sku,
                            stock: variant.inventory_quantity,
                            price: variant.price
                        }))
                    );

                    this.listadoProductos = variantsArray;

                    this.articulos = Array.from(
                        articulosShopify.flatMap(item =>
                            item.variants
                                .filter(variant => variant.sku)
                                .map(variant => {
                                    const image = item.images.find(img => img.id === variant.image_id) || {};
                                    const pedido = articulosPedido.find(art => art.sku === variant.sku) || {};

                                    let localizacion = pedido.localizacion == 1;

                                    return {
                                        sku: variant.sku,
                                        precio: parseFloat(variant.price),
                                        image: image.src || '',
                                        shopify_product_id: variant.id,
                                        stock: variant.inventory_quantity,
                                        stockIdeal: '',
                                        cantidades: [],
                                        confeccion: 0,
                                        confeccionUbrique: 0,
                                        localizacion: localizacion || false,
                                        pedidoActual: pedido.actual || '',
                                        pedidoAnual: pedido.anual || '',
                                        check: false
                                    };
                                })
                        ).reduce((acc, item) => {
                            acc.set(item.sku, item);
                            return acc;
                        }, new Map()).values()
                    );



                    // Procesar datos de finanzasArticulos
                    this.articulos.forEach(articulo => {
                        for (let precio of finanzasArticulos) {
                            if (articulo.sku.includes(precio.sku)) {
                                articulo.confeccion = precio.confeccion;
                                articulo.confeccionUbrique = precio.ubrique;
                                break;
                            }
                        }
                    });

                    // Procesar ventasMensual
                    const articulosMap = new Map();

                    //Optimizado
                    this.articulos.forEach(articulo => {
                        const cantidadesMap = new Map();
                        articulo.cantidades.forEach(cantidad => {
                            cantidadesMap.set(`${cantidad.anyo}_${cantidad.mes}`, cantidad);
                        });

                        articulo.cantidades = cantidadesMap;
                        articulosMap.set(articulo.sku, articulo);
                    });

                    ventasMensual.forEach(dato => {
                        const articulo = articulosMap.get(dato.sku);

                        if (articulo) {
                            const claveMesAnyo = `${dato.anyo}_${dato.mes}`;
                            const cantidadExistente = articulo.cantidades.get(claveMesAnyo);

                            if (cantidadExistente) {
                                cantidadExistente.total += dato.cantidad;
                            } else {
                                articulo.cantidades.set(claveMesAnyo, {
                                    anyo: dato.anyo,
                                    mes: dato.mes,
                                    total: dato.cantidad
                                });
                            }
                        }
                    });

                    this.articulos.forEach(articulo => {
                        articulo.cantidades = Array.from(articulo.cantidades.values());
                    });


                    // Calcular previsiones y cantidades por año
                    this.articulos.forEach(arti => {
                        this.calcularPrevisiones(arti);
                        this.anyos.forEach(element => {
                            this.getCantidadAnyo(arti, element.value);
                        });
                    });

                    this.articulosCargados = true;

                    this.articulos.sort((a, b) => a.sku.localeCompare(b.sku));

                })
                .catch(error => {
                    console.error("Error al realizar las llamadas a las API:", error);
                });
        },
        sortChanged(e) {
            this.lastSort = e;
            this.buscarFuncion();
        },
        buscarFuncion(items = this.listadoPedidos) {
            let auxSorting = JSON.parse(JSON.stringify(items));

            if (this.lastSort != null) {
                if (this.lastSort.sortDesc)
                    auxSorting.sort(
                        (a, b) => b[this.lastSort.sortBy] - a[this.lastSort.sortBy]
                    );
                else
                    auxSorting.sort(
                        (a, b) => a[this.lastSort.sortBy] - b[this.lastSort.sortBy]
                    );
            }

            items = auxSorting;

            this.pedidosBusqueda = [];

            this.pedidosBusqueda = items.filter((factura) => {
                const recibidoCondicion =
                    this.estadoSelect === "Todos" ||
                    (this.estadoSelect === "Sin Recibir" &&
                        factura.fechaRecibido === "") ||
                    (this.estadoSelect === "Recibido" && factura.fechaRecibido !== "");

                const pedidoCondicion =
                    this.estadoPedidoSelect === "Todos" ||
                    (this.estadoPedidoSelect === "Sin Pedir" &&
                        factura.fechaPedido === "") ||
                    (this.estadoPedidoSelect === "Pedido" && factura.fechaPedido !== "");

                const proveedorCondicion =
                    this.proveedorSelect.id === 0 ||
                    this.proveedorSelect.id === factura.idProveedor;

                const textoCondicion =
                    factura.codigo.toLowerCase().includes(this.buscar.toLowerCase()) ||
                    factura.proveedor.toLowerCase().includes(this.buscar.toLowerCase()) ||
                    factura.lista.toLowerCase().includes(this.buscar.toLowerCase());

                const fechaDesdeCondicion =
                    !this.fechaDesdeSelect ||
                    new Date(factura.fechaFactura) >= new Date(this.fechaDesdeSelect);
                const fechaHastaCondicion =
                    !this.fechaHastaSelect ||
                    new Date(factura.fechaFactura) <= new Date(this.fechaHastaSelect);

                return (
                    recibidoCondicion &&
                    pedidoCondicion &&
                    textoCondicion &&
                    proveedorCondicion &&
                    fechaDesdeCondicion &&
                    fechaHastaCondicion
                );
            });
        },
        async procesarClipBoard() {
            try {
                const rawData = await navigator.clipboard.readText();

                this.botonProcesarActivo = false;

                if (!rawData || !rawData.trim()) {
                    this.aviso('warning', 'Sin datos de Sold Out', 'Primero tienes que ir al SAAS y darle al botón de <b>Sold Out</b>; después vuelve aquí y pulsa <b>Procesar Sold Out</b>.')
                    this.botonProcesarActivo = true;
                    return;
                }

                const lines = rawData.trim().split("\n");
                const headers = lines[0].split(";");
                const skuIdx = headers.indexOf("Sku");
                const unitsIdx = headers.indexOf("Units");
                const stockIdx = headers.indexOf("Stock");

                // 1. Agrupamos primero los datos del CLIPBOARD
                const datosClipboard = lines.slice(1).reduce((acc, line) => {
                    const values = line.split(';');
                    const fullSku = values[skuIdx]?.trim();
                    const unidades = parseInt(values[unitsIdx]) || 0;
                    const stockCSV = values[stockIdx]?.trim() || "0";

                    if (fullSku) {
                        const lastDash = fullSku.lastIndexOf('-');
                        const raiz = fullSku.substring(0, lastDash);
                        const color = fullSku.substring(lastDash + 1).toLowerCase();

                        if (!acc[raiz]) acc[raiz] = { totalPedidos: 0, colores: {} };

                        acc[raiz].totalPedidos += unidades;
                        acc[raiz].colores[color] = {
                            unidades: (acc[raiz].colores[color]?.unidades || 0) + unidades,
                            stock: stockCSV
                        };
                    }
                    return acc;
                }, {});

                if (Object.keys(datosClipboard).length === 0) {
                    this.aviso('warning', 'Sin datos de Sold Out', 'Primero tienes que ir al SAAS y darle al botón de <b>Sold Out</b>; después vuelve aquí y pulsa <b>Procesar Sold Out</b>.')
                    this.botonProcesarActivo = true;
                    return;
                }

                // 2. Construimos el resultado final cruzando con THIS.ARTICULOS
                const resultadoFinal = Object.keys(datosClipboard).sort().map(raiz => {
                    const itemClipboard = datosClipboard[raiz];

                    // Objeto base
                    const fila = {
                        sku: raiz,
                        totalPedidos: itemClipboard.totalPedidos.toString(),
                        lineaPedido: true
                    };

                    // BUSCAMOS TODOS LOS COLORES DE ESTA RAÍZ EN EL MAESTRO DE ARTÍCULOS
                    // Asumiendo que la raíz se saca igual: SKU hasta el último guion
                    const variantesEnMaestro = this.articulos.filter(art => {
                        const lastDash = art.sku.lastIndexOf('-');
                        return art.sku.substring(0, lastDash) === raiz;
                    });

                    variantesEnMaestro.forEach(variante => {
                        const lastDash = variante.sku.lastIndexOf('-');
                        const color = variante.sku.substring(lastDash + 1).toLowerCase();
                        const skuCompleto = variante.sku;

                        // Agregar columna si no existe
                        this.agregarColumnaColor(color, variante);

                        // 1. Esperando (del clipboard)
                        fila[`esperando_${color}`] = itemClipboard.colores[color]?.unidades || 0;

                        // 2. Stock (Prioridad: clipboard si existe, si no, del maestro)
                        fila[`stock_${color}`] = itemClipboard.colores[color]?.stock || variante.stock || 0;

                        // 3. Previsión (del maestro)
                        fila[`prevision_${color}`] = variante.prevision || 0;

                        fila[`fabricando_${color}`] = this.articulosFabricando[skuCompleto] || 0;

                        // 4. Input editable
                        fila[`pedidos_${color}`] = '';
                    });

                    return fila;
                });

                this.pedidosSoldOut = resultadoFinal;
                //console.log("Procesado completo:", this.pedidosSoldOut);

            } catch (err) {
                console.error("Error:", err);
                this.aviso('error', 'No se pudo leer el portapapeles', 'Revisa que el navegador tenga permiso para leer el portapapeles e inténtalo de nuevo.');
                this.botonProcesarActivo = true;
            }
        },
        esc(texto) {
            return String(texto).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
        },
        aviso(icon, title, html) {
            return this.$swal({ icon, title, html, confirmButtonText: 'Entendido' });
        },
        unidadesFila(fila) {
            return Object.keys(fila)
                .filter(key => key.startsWith('pedidos_'))
                .reduce((total, key) => total + (parseInt(fila[key], 10) > 0 ? parseInt(fila[key], 10) : 0), 0);
        },
        async quitarFila(fila) {
            if (this.unidadesFila(fila) > 0) {
                const res = await this.$swal({
                    icon: 'question',
                    title: 'Quitar SKU',
                    html: `<b>${this.esc(fila.sku)}</b> tiene ${this.unidadesFila(fila)} uds puestas. ¿Quitarlo del pedido?`,
                    showCancelButton: true,
                    confirmButtonText: 'Quitar',
                    cancelButtonText: 'Cancelar',
                    confirmButtonColor: '#EF4444',
                });
                if (!res.isConfirmed) return;
            }
            const i = this.pedidosSoldOut.indexOf(fila);
            if (i > -1) this.pedidosSoldOut.splice(i, 1);
        },
        crearPedido() {
            const pedidosAValidar = [];

            this.pedidosSoldOut.forEach(item => {
                // 1. Buscamos todas las propiedades del objeto que empiecen por "pedidos_"
                const keys = Object.keys(item).filter(key => key.startsWith('pedidos_'));

                keys.forEach(key => {
                    const cantidadPedida = item[key];

                    // 2. Si el input tiene un valor y es mayor que 0
                    if (cantidadPedida && parseInt(cantidadPedida) > 0) {
                        const color = key.replace('pedidos_', '');

                        // 3. Construimos el objeto del ítem para el pedido
                        pedidosAValidar.push({
                            sku: `${item.sku}-${color.toUpperCase()}`,
                            cantidad: parseInt(cantidadPedida),
                        });
                    }
                });
            });

            if (pedidosAValidar.length === 0) {
                this.aviso('warning', 'Pedido vacío', 'Pon alguna cantidad en los colores antes de crear el pedido.');
                return;
            }

            const tipo = (this.$route.meta && this.$route.meta.tipo) || 'B2C';

            this.creando = true;
            axios
                .post("/taller/pedido/vero", {
                    items: pedidosAValidar,
                    tipo,
                    company: this.company.trim(),
                    escanerPedidoId: this.escanerPedidoId,
                })
                .then(() => {
                    this.$router.push(this.rutaListado);
                })
                .catch((error) => {
                    console.log(error);
                    this.aviso('error', 'No se pudo crear el pedido', 'Ha fallado al guardar en el servidor. Inténtalo de nuevo; si sigue fallando, avisa.');
                })
                .finally(() => {
                    this.creando = false;
                });
        },
        agregarSkuManual() {
            if (!this.skuManual || this.skuManual.trim() === '') return;

            const skuInput = this.skuManual.trim().toUpperCase();

            // Determinar la raíz del SKU (sin el color)
            // El usuario puede ingresar "MS-PBF-11" o "MS-PBF-11-TAN"
            let raiz = skuInput;

            // Raíz = SKU sin el color (lo que va tras el último guion)
            const raizDe = sku => sku.substring(0, sku.lastIndexOf('-'));
            const deRaiz = r => this.articulos.filter(art => raizDe(art.sku.toUpperCase()) === r);

            // Si se escribe la raíz se traen todos sus colores; si se escribe un SKU
            // completo (ej. WALLET-BIFOLD-BLK) también, sacando la raíz de ese SKU
            let variantesEncontradas = deRaiz(skuInput);
            if (variantesEncontradas.length === 0 && this.articulos.some(art => art.sku.toUpperCase() === skuInput))
                variantesEncontradas = deRaiz(raizDe(skuInput));

            if (variantesEncontradas.length === 0) {
                this.aviso('warning', 'SKU no encontrado', `No hay ningún artículo con el SKU <b>${this.esc(skuInput)}</b> en Shopify.`);
                return;
            }

            // Obtener la raíz desde el primer resultado
            const primerSku = variantesEncontradas[0].sku.toUpperCase();
            const lastDash = primerSku.lastIndexOf('-');
            raiz = primerSku.substring(0, lastDash);

            // Verificar si ya existe en la lista
            const yaExiste = this.pedidosSoldOut.find(item => item.sku.toUpperCase() === raiz);
            if (yaExiste) {
                this.aviso('info', 'Ya está en la lista', `<b>${this.esc(raiz)}</b> ya está en el pedido.`);
                this.skuManual = '';
                return;
            }

            // Crear la fila con todas las variantes de color
            const fila = {
                sku: raiz,
                totalPedidos: '0',
                lineaPedido: true
            };

            variantesEncontradas.forEach(variante => {
                const varLastDash = variante.sku.lastIndexOf('-');
                const color = variante.sku.substring(varLastDash + 1).toLowerCase();
                const skuCompleto = variante.sku;

                // Agregar columna si no existe
                this.agregarColumnaColor(color);

                fila[`esperando_${color}`] = 0;
                fila[`stock_${color}`] = variante.stock || 0;
                fila[`prevision_${color}`] = variante.prevision || 0;
                fila[`fabricando_${color}`] = this.articulosFabricando[skuCompleto] || 0;
                fila[`pedidos_${color}`] = '';
            });

            this.pedidosSoldOut.push(fila);
            this.pedidosSoldOut.sort((a, b) => a.sku.localeCompare(b.sku));

            this.botonProcesarActivo = false;
            this.skuManual = '';
        },
        agregarColumnaColor(color, variante = null) {
            const colorLower = color.toLowerCase();
            const columnaKey = `pedidos_${colorLower}`;

            // Verificar si la columna ya existe
            const existe = this.columnasTable.some(col => col.key === columnaKey);

            if (!existe) {
                // Orden específico de colores
                const ordenColores = ['tan', 'black', 'blk', 'db', 'navy', 'grey', 'mocha', 'olive', 'camel', 'caoba', 'brown', 'army', 'amber', 'stone', 'hab', 'khaki', 'khk'];

                // Capitalizar el nombre del color para el label
                const label = color.charAt(0).toUpperCase() + color.slice(1).toLowerCase();

                const nuevaColumna = {
                    key: columnaKey,
                    label: label
                };

                // Encontrar la posición correcta para insertar
                const indexNuevo = ordenColores.indexOf(colorLower);

                if (indexNuevo === -1) {
                    // Color no está en la lista, agregar al final
                    this.columnasTable.push(nuevaColumna);
                } else {
                    // Buscar la posición correcta entre las columnas existentes
                    let insertIndex = this.columnasTable.length;

                    for (let i = 1; i < this.columnasTable.length; i++) {
                        const col = this.columnasTable[i];
                        const colColor = col.key.replace('pedidos_', '');
                        const colIndex = ordenColores.indexOf(colColor);

                        if (colIndex === -1 || colIndex > indexNuevo) {
                            insertIndex = i;
                            break;
                        }
                    }

                    this.columnasTable.splice(insertIndex, 0, nuevaColumna);
                }
            }
        },
        calcularPrevisiones(item) {

            let total = 0;

            let auxAnyos = JSON.parse(JSON.stringify(this.anyos));

            if (auxAnyos[0].value == new Date().getFullYear()) {
                let valido = true;

                let mesActual = new Date().getMonth() + 1;
                this.meses.forEach(mes => {
                    if (mes.active && mes.value >= mesActual)
                        valido = false;
                });

                if (!valido)
                    auxAnyos.shift();
            }

            auxAnyos = auxAnyos.filter(function (obj) {
                return obj.active == true;
            });

            auxAnyos.forEach(anyo => {
                if (anyo.active) {
                    anyo.total = 0;

                    this.meses.forEach(mes => {
                        if (mes.active) {
                            let cantidad = item.cantidades.filter(function (el) {
                                return el.anyo == anyo.value && el.mes == mes.value;
                            });

                            if (cantidad.length > 0) {
                                anyo.total += cantidad[0].total
                            }
                        }
                    });
                }
            });

            let startCount = false;

            let cantidadAnyos = 0;
            let porcentajeAnyos = 0;

            let ultimoValorValido = 0;

            for (let y = auxAnyos.length - 1; y >= 0; y--) {
                if (auxAnyos[y].active && (auxAnyos[y].total > 0 || startCount)) {
                    cantidadAnyos++;
                    startCount = true;
                    if (y > 0) {
                        let aux = auxAnyos[y - 1].total - auxAnyos[y].total;
                        porcentajeAnyos += aux;
                    }

                    ultimoValorValido = auxAnyos[y].total;
                }
            }


            total = porcentajeAnyos / cantidadAnyos;

            if (total > 0) {
                total = ultimoValorValido + total;
            }
            else {
                total = ultimoValorValido - total;
            }

            if (isNaN(total))
                total = "";
            else
                total = Math.round(total);

            item.prevision = total;
            item.stockIdeal = total - item.stock;

            if (item.stockIdeal <= 0)
                item.stockIdeal = "";
        },
        getCantidadAnyo(item, anyo) {

            let total = 0;

            let tieneValor = false;

            this.meses.forEach(mes => {

                if (mes.active) {

                    let cantidad = item.cantidades.filter(function (el) {
                        return el.anyo == anyo && el.mes == mes.value;
                    });

                    if (cantidad.length > 0) {
                        total += cantidad[0].total
                        tieneValor = true;
                    }

                }

            });

            if (total == 0 && !tieneValor)
                total = "";

            item[anyo] = total;

            return total;

        },
    },
    mounted() {
        this.loadItems();
        if (this.esB2B) this.cargarPedidosEscaner();
    },
    watch: {
        buscar() {
            if (this.buscar == "") this.countItemView = this.defaultItemView;
            this.buscarFuncion();
        },
    },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import './pv-base.scss';

// ══════════ NUEVO PEDIDO (mismo estilo que el listado) ══════════
.pv.pvn {

  .pv-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  // ──────────── Formulario ────────────
  .pvn-form {
    display: flex;
    flex-wrap: wrap;
    gap: 14px 18px;
    margin-top: 16px;
    padding-top: 14px;
    border-top: 1px solid $pv-divider;
  }

  .pvn-field {
    display: flex;
    flex-direction: column;
    gap: 6px;

    label {
      font-family: 'JetBrains Mono', monospace;
      font-size: 10px;
      font-weight: 600;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      color: $pv-soft;
      margin: 0;
    }

    &--wide { flex-basis: 100%; }
  }

  .pvn-inline {
    display: flex;
    gap: 8px;
  }

  .pvn-input {
    flex: none; // .pv-search trae flex-basis 320px, que en columna se vuelve altura
    width: 260px;
    max-width: 100%;
  }

  .nv-select {
    min-width: 460px;
    max-width: 100%;

    .vs__dropdown-menu { min-width: 100%; width: auto; }
    .vs__dropdown-option { white-space: nowrap; }
  }

  // ──────────── Leyenda ────────────
  .pvn-legend {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
  }

  .pvn-tag {
    font-family: 'JetBrains Mono', monospace;
    font-size: 10px;
    font-weight: 600;
    padding: 3px 8px;
    border-radius: 999px;
    letter-spacing: 0.04em;

    &--stock  { background: rgba(16, 185, 129, 0.1);  color: $pv-emerald; }
    &--prev   { background: rgba(59, 130, 246, 0.1);  color: $pv-primary; }
    &--fabri  { background: rgba(139, 92, 246, 0.1);  color: #8B5CF6; }
    &--orders { background: rgba(245, 158, 11, 0.12); color: #B45309; }
  }

  // ──────────── Tabla ────────────
  .pvn-table {
    thead th.pvn-th-color,
    tbody td.pvn-td-color {
      text-align: center;
      padding: 8px 4px !important;
      min-width: 82px;
    }

    thead th.pvn-th-sku,
    tbody td.pvn-td-sku {
      min-width: 190px;
      padding-left: 18px !important;
    }

    thead th.pvn-th-acc,
    tbody td.pvn-td-acc {
      width: 48px;
      text-align: right;
      padding-right: 14px !important;
    }

    tbody tr:hover { background: rgba(59, 130, 246, 0.03) !important; }
  }

  .pvn-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding: 44px 0;
    color: $pv-mute;
    font-size: 13px;

    i { font-size: 26px; color: $pv-soft; }
  }

  .pvn-sku {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .pvn-sku-code {
    font-family: 'JetBrains Mono', monospace;
    font-size: 12px;
    font-weight: 700;
    color: $pv-ink;
  }

  .pvn-sku-uds {
    font-size: 11px;
    font-weight: 600;
    color: $pv-primary;
  }

  .pvn-cell {
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    gap: 3px;
    padding: 6px 6px 5px;
    border: 1px solid transparent;
    border-radius: 8px;
    transition: background 150ms ease, border-color 150ms ease;

    &--orders { background: rgba(245, 158, 11, 0.08); }
    &--activa { border-color: rgba(59, 130, 246, 0.35); background: rgba(59, 130, 246, 0.05); }
  }

  // Etiqueta corta delante del número (Stock, Prev, Fab, Ord)
  .pvn-stock em,
  .pvn-meta em {
    font-style: normal;
    font-weight: 500;
    opacity: 0.75;
    margin-right: 3px;
  }

  .pvn-stock {
    font-family: 'JetBrains Mono', monospace;
    font-size: 11px;
    font-weight: 700;
    line-height: 1;

    &.is-ok  { color: $pv-emerald; }
    &.is-out { color: $pv-coral; }
  }

  .pvn-qty {
    width: 46px;
    height: 28px;
    padding: 0 2px;
    text-align: center;
    font-family: 'JetBrains Mono', monospace;
    font-size: 13px;
    font-weight: 700;
    color: $pv-ink;
    background: $pv-surface;
    border: 1px solid $pv-border;
    border-radius: 6px;
    outline: none;
    -moz-appearance: textfield;
    transition: border-color 150ms ease, box-shadow 150ms ease;

    &::-webkit-inner-spin-button,
    &::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }

    &:focus {
      border-color: $pv-primary;
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
    }
  }

  .pvn-meta {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 3px;
    min-height: 15px;

    span {
      font-family: 'JetBrains Mono', monospace;
      font-size: 10px;
      font-weight: 600;
      line-height: 1;
      padding: 2px 4px;
      border-radius: 4px;
    }
  }

  .pvn-prev   { background: rgba(59, 130, 246, 0.1);  color: $pv-primary; }
  .pvn-fabri  { background: rgba(139, 92, 246, 0.1);  color: #8B5CF6; }
  .pvn-orders { background: rgba(245, 158, 11, 0.14); color: #B45309; }

  .pvn-na {
    color: $pv-border;
    font-size: 14px;
  }

  .pvn-remove {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 6px;
    border: 1px solid $pv-border;
    color: $pv-soft;
    cursor: pointer;
    transition: all 150ms ease;

    &:hover { background: $pv-coral; border-color: $pv-coral; color: #fff; }
  }
}

// ══════════ MODO OSCURO ══════════
.dark-layout .pv.pvn {
  .pvn-form { border-top-color: #2c2c2c; }
  .pvn-sku-code { color: #e4e6eb; }
  .pvn-qty { background: #161616; border-color: #2c2c2c; color: #e4e6eb; }
  .pvn-cell--activa { background: rgba(59, 130, 246, 0.12); }
  .pvn-cell--orders { background: rgba(245, 158, 11, 0.12); }
  .pvn-orders, .pvn-tag--orders { color: #FBBF24; }
  .pvn-na { color: #3a3a3a; }
  .pvn-remove { border-color: #2c2c2c; color: #8e9099; }
}

@media (max-width: 575px) {
  .pv.pvn .nv-select { min-width: 100%; }
  .pv.pvn .pvn-input { width: 100%; }
}
</style>
